import React, { useCallback, useEffect, useMemo, useState } from "react"
import { ButtonGroup } from "dash-styled"
import {
    APIJobModel,
    JobVersionItem
} from "../../../../../../Models/API/JobModel"
import {
    getTaskID,
    LabelerType,
    TaskModel,
    UITaskModel
} from "../../../../../../Models/API/TaskModel"
import { TabButton } from "../../../../../Common/Button/TabButton"
import { taskColumns } from "../DetailView/Task/TaskList/TaskColumns"
import { Table } from "resha"
import { JobDetailViewDetailsSection } from "../DetailView/Job/DetailsSection/DetailsSection"

interface DetailViewSidebarProps {
    appID: string
    job: APIJobModel
    taskList: UITaskModel[]
    onTaskChange?: (taskID: string) => void
    versionsList: JobVersionItem[]
}

export function JobDetailViewSidebar({
    appID,
    job,
    taskList,
    onTaskChange,
    versionsList
}: DetailViewSidebarProps) {
    const isTaskAvailable = taskList.length > 0
    const [tab, setTab] = useState<"tasks" | "details">(
        isTaskAvailable ? "tasks" : "details"
    )

    const cols = useMemo(
        () =>
            taskColumns({
                appID,
                isCollaboratorTask:
                    taskList.length > 0 &&
                    taskList[0].labelerType === LabelerType.collaborator
            }),
        [appID, taskList]
    )

    const handleClickTaskRow = useCallback(
        (task: TaskModel) => {
            onTaskChange?.(task.id)
        },
        [onTaskChange]
    )

    useEffect(() => {
        if (isTaskAvailable) {
            setTab("tasks")
        }
    }, [isTaskAvailable])

    return (
        <div
            className="flex flex-col justify-start h-full"
            data-testid="detail-view-sidebar"
        >
            <ButtonGroup equalWidth={true}>
                {isTaskAvailable ? (
                    <TabButton
                        onClick={() => setTab("tasks")}
                        active={tab === "tasks"}
                    >
                        Tasks
                    </TabButton>
                ) : null}
                <TabButton
                    onClick={
                        isTaskAvailable ? () => setTab("details") : undefined
                    }
                    active={tab === "details"}
                    disabled={!isTaskAvailable}
                >
                    Details
                </TabButton>
            </ButtonGroup>
            {tab === "tasks" && (
                <>
                    {taskList.length === 0 ? (
                        <p className="mt-6 text-center text-lg">
                            This job doesn't have tasks
                        </p>
                    ) : (
                        <div className="m-3 overflow-auto flex-grow">
                            <Table<UITaskModel>
                                columns={cols}
                                items={taskList}
                                itemCount={taskList.length}
                                getKey={getTaskID}
                                onRowClick={handleClickTaskRow}
                                className="w-full"
                                checkedItemIds={[]}
                            />
                        </div>
                    )}
                </>
            )}
            {tab === "details" && (
                <JobDetailViewDetailsSection
                    job={job}
                    versionsList={versionsList}
                />
            )}
        </div>
    )
}
